import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';
import { eventsAtom, Event } from '../atoms-types/events';
import ConvertTime from '../converters/timestampConverter';
import useWebSocket from '../../hooks/wsHook';
import EventNewsfeedGrid from '../grids/eventNewsfeedGrid';
import EventTable from '../charts-graphs/eventTable';
import GraphTimePicker from '../buttons/graphTimePicker';
import moment, { Moment } from 'moment-timezone';
import { endpointLink, websocketLink } from '../../index';

export default function EventNewsfeedHandler() {
  const theme = useTheme();
  const { messages } = useWebSocket(websocketLink, ['event']);
  const setEventData = useSetAtom(eventsAtom);
  const [startDateTime, setStartDateTime] = useState<Moment>(moment().subtract(24, 'hours'));
  const [endDateTime, setEndDateTime] = useState<Moment>(moment());
  const [selectedRange, setSelectedRange] = useState<string>('Last 24 hours');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [noEventsFound, setNoEventsFound] = useState(false);

  // Initial fallback events query
  const { isFetching: isFallbackFetching, isError: isFallbackError, error: fallbackError } = useQuery({
    queryKey: ['fallbackEvents'],
    staleTime: Infinity,
    enabled: isInitialLoad,
    queryFn: async () => {
      const response = await axios.get<Event[]>(`${endpointLink}fallback-events`);
      const data = response.data;
      UpdateEventData(data);
      setIsInitialLoad(false);
      return data;
    }
  });

  const fetchEventsByTimeRange = async (start: Moment, end: Moment) => {
    if (isInitialLoad) return; // Skip if still loading initial data
    try {
      const response = await axios.get<Event[]>(`${endpointLink}select-event`, {
        params: {
          start: start.toISOString(),
          end: end.toISOString()
        }
      });
      UpdateEventData(response.data);
    } catch (error) {
      console.error('Error fetching events by time range:', error);
    }
  };

  function UpdateEventData(data: Event[]) {
    if (!data || data.length === 0) {
      console.log("No events found for the selected timeframe");
      setEventData([]); // Clear the atom when no data is found
      setNoEventsFound(true);
      return;
    }

    setNoEventsFound(false);
    const updatedEventData = data?.map(event => ({
      ...event,
      Timestamp: ConvertTime(event.Timestamp)
    }));
    setEventData(updatedEventData);
  }

  useEffect(() => {
    messages.forEach((messageObj) => {
      const { channel, message } = messageObj;

      if (channel === 'event') {
        console.log("received new message from: ", channel);
        try {
          if (message && typeof message === "object") {
            console.log('Updating event data:', message);
            setEventData((prev) => [
              ...prev, 
              message as Event
            ]);
          } else {
            console.error('Invalid event payload format:', message);
          }
        } catch (error) {
          console.error('Failed to parse message:', error);
        }
      }
    });
  }, [messages, setEventData]);

  useEffect(() => {
    fetchEventsByTimeRange(startDateTime, endDateTime);
  }, [startDateTime, endDateTime]);

  const renderLoadingOrError = () => {
    if (isFallbackFetching) {
      return <p>Loading initial data...</p>;
    } else if (isFallbackError) {
      return <p>Error: {fallbackError.message}</p>;
    }
    return null;
  };

  return (
    <div>
      {renderLoadingOrError()}
      <div style={{ marginBottom: '20px', marginTop: '20px' }}>
        <GraphTimePicker
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          setStartDateTime={setStartDateTime}
          setEndDateTime={setEndDateTime}
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
        />
      </div>
      {noEventsFound ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          fontSize: '18px', 
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          No events were found during this timeframe.
        </div>
      ) : (
        <EventTable/>
      )}
    </div>
  );
}